import { z } from 'zod';

const version = z.number().int().positive().max(Number.MAX_SAFE_INTEGER);
const identity = z
  .object({
    owner_id: z.string().uuid(),
    case_execution_id: z.string().uuid(),
    attempt: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
    generation_version: version,
  })
  .strict();
const traceRef = z
  .object({
    state: z.enum([
      'indexed',
      'missing_manifest',
      'ambiguous',
      'version_identity_unavailable',
    ]),
    key: z.string().min(1).max(2048).nullable(),
    identity: identity.nullable(),
  })
  .strict()
  .superRefine((ref, ctx) => {
    if (ref.state !== 'indexed') {
      if (ref.key !== null || ref.identity !== null)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Unavailable trace has a descriptor',
        });
      return;
    }
    if (!ref.key || !ref.identity) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Indexed trace needs identity',
      });
      return;
    }
    const prefix = `owners/${ref.identity.owner_id}/cases/${ref.identity.case_execution_id}/attempt-${ref.identity.attempt}/artifacts/`;
    if (
      !ref.key.startsWith(prefix) ||
      !/^[a-f0-9]{64}\/trace\.jsonl$/.test(ref.key.slice(prefix.length))
    )
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Trace namespace and identity disagree',
      });
  });
const seed = z
  .object({ structured_key: z.string(), rendered_key: z.string().nullable() })
  .strict()
  .nullable();
const entry = z
  .object({
    version,
    mode: z.enum(['synthesize', 'refine']),
    seed_prompt: seed,
    trace_ref: traceRef,
  })
  .strict()
  .refine(
    (value) =>
      !value.trace_ref.identity ||
      value.trace_ref.identity.generation_version === value.version,
    'Generation version mismatch',
  );
/** The selected v2 envelope is closed; existing source payloads retain their own unchanged contracts. */
export const generationBrowserContextSchema = z
  .object({
    generation_plan: z.unknown().optional(),
    schema_version: z.literal(2),
    projection: z.literal('browser_v1'),
    run_id: z.string().uuid(),
    run_kind: z.literal('generation'),
    manifest: z.unknown().nullable(),
    versions: z.array(entry),
    inputs: z.array(
      z
        .object({
          key: z.string(),
          filename: z.string(),
          kind: z.string(),
          content_type: z.string().optional(),
        })
        .strict(),
    ),
    workspace_knowledge: z.unknown().nullable().optional(),
    source_controls: z
      .object({
        sourceControlVersion: z.literal(1).optional(),
        useWorkspaceKnowledge: z.boolean().optional(),
        knowledgeInstructions: z.string().max(2000).optional(),
        knowledgeMaxRequests: z.number().int().min(1).max(20).optional(),
        knowledgeChunksPerRequest: z.number().int().min(1).max(5).optional(),
        useArtifacts: z.boolean().optional(),
        artifactIds: z.array(z.string().uuid()).optional(),
        useMemory: z.boolean().optional(),
        memoryShared: z.boolean().optional(),
        memoryPersonal: z.boolean().optional(),
      })
      .strict()
      .optional(),
    authoring_environment_name: z.string().nullable().optional(),
    exploration: z
      .object({
        mode: z.enum(['custom', 'named', 'none']),
        environmentName: z.string().nullable(),
        url: z.string().nullable(),
      })
      .strict()
      .optional(),
    resolved_context_scope: z.unknown().nullable().optional(),
  })
  .strict()
  .refine(
    (value) =>
      new Set(value.versions.map((item) => item.version)).size ===
      value.versions.length,
    'Duplicate generation versions',
  );
